import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type Row, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { guardNumber } from "@msuite/utils/numbers";
import type { TeamListItem } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { client } from "@/hono";
import { EditTeamModal } from "@/modals/organizations/edit-team-modal";

/** Props Interface */
interface TeamGroupHeaderProps {
	item: Row<TeamListItem>;
}

export function TeamGroupHeader({ item }: TeamGroupHeaderProps) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		toast.promise(
			async () => {
				const response = await client.organizations.teams[":teamId"].$delete({
					param: { teamId: item.original.teamId },
				});
				await responseHandler(response);
				await refetch?.();
			},
			{
				loading: t("general.deleting"),
				success: t("general.deleted_successfully"),
				error: t("general.deleting_failed"),
			},
		);
	}

	/** Computed */
	// Deleting a team Personio's import created also keeps the import from creating it again.
	const deleteMessage =
		item.original.source === "personio"
			? t("organization.delete_personio_team_confirmation")
			: t("organization.delete_team_confirmation");

	/** Render */
	return (
		<Fragment>
			<div className="h-stack justify-between">
				<p>{item.original.name}</p>
				<div className="flex-1" />
				{guardNumber(item.original.teamLeaders.length) > 0 ? (
					<div className="h-stack">
						<Icon name="User" />
						<p>
							{guardArray(item.original.teamLeaders)
								.map((user) => getFullName(user))
								.join(", ")}
						</p>
					</div>
				) : null}
				<div className="self-start pt-1 -my-2">
					<Menu
						items={createStandardMenuItems({
							onEdit: disclosure.onOpen,
							onDelete: handleOnDelete,
						})}
					>
						<Button
							size="xxs"
							variant="ghost"
							icon={<Icon name="EllipsisVertical" />}
						/>
					</Menu>
				</div>
			</div>
			{disclosure.isOpen && (
				<EditTeamModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					teamId={item.original.teamId}
					onComplete={refetch}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					title={t("organization.delete_team")}
					message={deleteMessage}
				/>
			)}
		</Fragment>
	);
}
